import type { Period } from 'types/periods'
// material-ui
import { Grid2 as Grid, Stack } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Paper from 'components/@extended/Paper'

import { TextField } from 'components/@extended/TextField'

import dayjs from 'dayjs'

import { ThemeMode } from 'types/config'
import { createIdToPeriod, DATE_FORMAT } from 'types/periods'
import { currencyFormatter, twoDigitPercentFormatter } from 'utils/formatting'
import CompensationSummaryTile from './CompensationSummaryTile'

// ==============================|| REPORT CARD ||============================== //
interface CompensationSummaryCardProps {
  compensationModel?: string
  variablePercent?: number
  baseSalary?: number
  variableCap?: number
  name?: string
  position?: string
  effectiveDate?: string
  periodProration?: number
  periodId?: string
  period?: Period
}

function CompensationSummaryCard({
  compensationModel,
  variablePercent,
  baseSalary,
  variableCap,
  periodProration = 1.0,
  name,
  position,
  effectiveDate,
  periodId,
  period,
}: CompensationSummaryCardProps) {
  const theme = useTheme()

  const footerBackgroundColor =
    theme.palette.mode === ThemeMode.LIGHT ? theme.palette.grey[300] : theme.palette.grey[300]

  const footerTextColor =
    theme.palette.mode === ThemeMode.LIGHT ? theme.palette.info.contrastText : theme.palette.primary.contrastText

  let periodLabel = ''
  if (period) {
    const periodMap = createIdToPeriod(period)
    const compPeriod = periodMap[periodId!]

    if (compPeriod) {
      periodLabel = compPeriod.label
    }
  }

  return (
    <>
      <Stack spacing={5}>
        {name &&
          position &&
          effectiveDate &&
          periodLabel != '' &&
          compensationModel !== undefined &&
          compensationModel != null &&
          baseSalary !== undefined &&
          baseSalary != null &&
          variablePercent !== undefined &&
          variablePercent != null &&
          variableCap !== undefined &&
          variableCap != null &&
          periodProration !== undefined &&
          periodProration != null && (
            <Grid size={{ xs: 12 }}>
              <Paper>
                <Grid container sx={{ padding: { xs: 2, sm: 3 } }} spacing={3}>
                  {/* Left Section: Metadata */}
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Grid container spacing={2}>
                      {/* Employee Info Row */}
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Employee Name"
                          value={name}
                          slotProps={{
                            input: {
                              readOnly: true,
                            },
                          }}
                          fullWidth
                          focused
                          variant="outlined"
                          pill
                          shrink
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Position"
                          value={position}
                          slotProps={{
                            input: {
                              readOnly: true,
                            },
                          }}
                          fullWidth
                          focused
                          variant="outlined"
                          pill
                          shrink
                        />
                      </Grid>

                      {/* Period Info Row */}
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Compensation Period"
                          value={periodLabel}
                          slotProps={{
                            input: {
                              readOnly: true,
                            },
                          }}
                          fullWidth
                          focused
                          variant="outlined"
                          pill
                          shrink
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Effective Date"
                          value={dayjs.utc(effectiveDate).format(DATE_FORMAT)}
                          slotProps={{
                            input: {
                              readOnly: true,
                            },
                          }}
                          fullWidth
                          focused
                          variant="outlined"
                          pill
                          shrink
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Right Section: Compensation Summary Tiles */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Stack spacing={2}>
                      <CompensationSummaryTile
                        label="Base Compensation"
                        value={baseSalary * periodProration}
                        formatter={currencyFormatter}
                        width="100%"
                        border
                        borderColor={footerBackgroundColor}
                        backgroundColor="transparent"
                        footer={
                          periodProration < 1
                            ? compensationModel === 'BASE_PLUS'
                              ? `Prorated from an Annual Base Salary of ${currencyFormatter.format(baseSalary)}`
                              : `Prorated from an Expected Total Annual Earnings of ${currencyFormatter.format(baseSalary)}`
                            : undefined
                        }
                      />
                      <CompensationSummaryTile
                        label="Variable Compensation"
                        value={`${currencyFormatter.format(
                          compensationModel === 'BASE_PLUS'
                            ? baseSalary * variablePercent * periodProration
                            : baseSalary * variablePercent * periodProration
                        )} (${twoDigitPercentFormatter.format(variablePercent)})`}
                        formatter={null}
                        width="100%"
                        border
                        borderColor={footerBackgroundColor}
                        footer={`Up to ${currencyFormatter.format(
                          baseSalary * variableCap * variablePercent * periodProration
                        )} (Max)`}
                        footerTextColor={footerTextColor}
                        footerBackgroundColor={footerBackgroundColor}
                        backgroundColor="transparent"
                      />

                      <CompensationSummaryTile
                        label="On-Target Total Compensation"
                        value={
                          compensationModel === 'BASE_PLUS'
                            ? baseSalary * periodProration + baseSalary * variablePercent * periodProration
                            : baseSalary * periodProration * (1 - variablePercent) +
                              baseSalary * variablePercent * periodProration
                        }
                        formatter={currencyFormatter}
                        width="100%"
                        border
                        borderColor={footerBackgroundColor}
                        footer={`Up to ${currencyFormatter.format(
                          compensationModel === 'BASE_PLUS'
                            ? baseSalary * periodProration +
                                baseSalary * variableCap * variablePercent * periodProration
                            : baseSalary * periodProration * (1 - variablePercent) +
                                baseSalary * variableCap * variablePercent * periodProration
                        )} (Max)`}
                        footerTextColor={footerTextColor}
                        footerBackgroundColor={footerBackgroundColor}
                        backgroundColor="transparent"
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
      </Stack>
    </>
  )
}

export default CompensationSummaryCard
